/**
 * Route-level feed-need fallback (consumer feed filters, Phase 2).
 *
 * The Phase-2 `sports` allow-list is built from what mounted sources declare
 * they render. Every audited route now declares for itself (its view names
 * its sports, its games, or its rows by ticker) or renders no live market
 * row at all — feed-filters-per-view, 2026-09-12, audit table in
 * `docs/agents/handoffs/feed-filters-per-view/handoff.md`. Before that audit
 * this fallback declared `sports: 'all'` for every route outside the
 * dashboard and a game detail, and on Saturday CFB 22 of 33 gbuff sockets on
 * he2 were the Position Tracker, the account plane and the fallback routes
 * each pulling the full ~1,800 frames/s slate.
 *
 * Only an UNKNOWN route still falls back to `sports: 'all'`: a view nobody
 * has audited must not be starved by a sibling tab's narrow declaration.
 * Adding a route means adding it here — to `SELF_DECLARING_PREFIXES` if its
 * view declares, to `NON_MARKET_PREFIXES` if it renders no live market rows —
 * or it pulls the firehose. Inert (declares nothing worth sending) unless
 * VITE_FEED_FILTERS_SPORTS is on; class/type opt-ins are unaffected.
 */
import { useLocation } from 'react-router';
import { useFeedNeeds } from '@/hooks/useFeedNeeds';

/** Routes whose view declares its own needs (a sport set, a game, or its
 *  rows by ticker). The fallback must stay silent here — an omitted axis
 *  means every sport, which would cancel the view's own narrowing. */
const SELF_DECLARING_PREFIXES = [
  '/dashboard',              // ValueDashboard: the selected sport chip
  '/game',                   // GameDetail / Sources: the sport + the event
  '/live',                   // LiveView: every sport, moneylines (explicit)
  '/positions',              // PositionTracker / TraderDetail / OrgDetail: held rows by ticker
  '/trades',                 // MyTrades: the traded tickers
  '/organization/org-account',          // OrgDetail
  '/organization/org-settings/trader',  // TraderDetail
  '/research/streamrift',    // the Eye: LIVE_RESEARCH_SPORTS
  '/research/g3',            // G3 live tabs: LIVE_RESEARCH_SPORTS
  '/prototypes/prop-workbench', // MLB + NFL player props
  '/bots/g9',                // G9: its sport list (all in fallback discovery)
  '/bots/vanilla',           // VanillaMM: VANILLA_SPORTS
  '/bots/harry',             // Harry: VANILLA_SPORTS
];

/** Routes whose views render no live market rows (REST / relay only, or
 *  transport metadata). Their fallback must not widen the shared feed for
 *  every trading tab (#853). Prefix-matched, like the list above. */
const NON_MARKET_PREFIXES = [
  '/settings',
  '/administration',         // management, G1 Health (relay diagnostics)
  '/organization',           // org-settings, trader-results, exports, cash ledger (the two market-reading pages above are matched first)
  '/research/g4',            // analytics REST only
  '/bots/marty', '/bots/rita', '/bots/recycler', '/bots/automation', // relay REST only
];

function matches(pathname: string, prefixes: readonly string[]): boolean {
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function FeedNeedsRouteFallback() {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, '') || '/';
  const silent = matches(path, SELF_DECLARING_PREFIXES) || matches(path, NON_MARKET_PREFIXES);
  useFeedNeeds('route', silent ? null : { sports: 'all' });
  return null;
}
